import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { Button, Typography } from "@platform/design-system";
import { useCallback, useEffect, useMemo, useState, type JSX } from "react";

import {
  AGE_BANDED_STRUCTURE_DETAILS,
  COVERAGE_TIERS,
  DEFAULT_EMP_SPOUSE_RATES_DIFFER,
  DEFAULT_TIER_COUNT,
  DEFAULT_TIERS_BY_COUNT,
  EMP_SPOUSE_OPTIONS,
  RATE_STRUCTURE,
  STRUCTURE_DETAILS,
  TIER_COUNTS,
} from "../../constants";
import { RateScheduleFormData } from "../../types/models";
import {
  getAvailableRateStructures,
  getRateStructureFlags,
} from "../../utils/rate-helpers";

import { AdndRateConfigSection } from "./AdndRateConfigSection";
import { AgeBandRow } from "./AgeBandRow";
import { AgeBandedRateSection } from "./AgeBandedRateSection";
import { FlatRateConfigSection } from "./FlatRateConfigSection";
import {
  applyRateStructureChange,
  EMPLOYEE_DEPENDENT_VARIANTS,
  mapTierFromBackendValue,
} from "./rate-helpers";

// Re-export for backwards compatibility with existing imports
export { mapTierFromBackendValue };

// Reverse map backend unit enum to UI fields (coveredBenefitType + ratePer)
export const mapFromBackendUnit = (
  unit: string,
): {
  coveredBenefitType: "coveredBenefit" | "coveredPayroll";
  ratePer: "per10" | "per100" | "per1000";
} => {
  switch (unit) {
    case "per10OfSalary":
      return { coveredBenefitType: "coveredPayroll", ratePer: "per10" };
    case "per100OfSalary":
      return { coveredBenefitType: "coveredPayroll", ratePer: "per100" };
    case "per1000OfSalary":
      return { coveredBenefitType: "coveredPayroll", ratePer: "per1000" };
    case "volumePer10":
      return { coveredBenefitType: "coveredBenefit", ratePer: "per10" };
    case "volumePer100":
      return { coveredBenefitType: "coveredBenefit", ratePer: "per100" };
    case "volumePer1000":
    default:
      return { coveredBenefitType: "coveredBenefit", ratePer: "per1000" };
  }
};

interface RatesTabProps {
  formData: RateScheduleFormData;
  setFormData: React.Dispatch<React.SetStateAction<RateScheduleFormData>>;
  /** Coverage type to determine available rate structures */
  coverageType?: string;
}

interface GenerateRangesParams {
  youngest: number;
  oldest: number;
  value: number;
  method: "age-gap" | "bands";
}

function computeAgeBandRanges({
  youngest,
  oldest,
  value,
  method,
}: GenerateRangesParams): { lower: number; upper: number }[] {
  if (
    !Number.isFinite(youngest) ||
    !Number.isFinite(oldest) ||
    !Number.isFinite(value)
  ) {
    return [];
  }

  if (youngest > oldest || value <= 0) {
    return [];
  }

  const computed: { lower: number; upper: number }[] = [];

  // Add lower bound band (<youngest) — shares boundary value with the next band
  if (youngest > 0) {
    computed.push({ lower: 0, upper: youngest });
  }

  if (method === "age-gap") {
    let start = youngest;
    while (start <= oldest) {
      const end = Math.min(start + value - 1, oldest);
      computed.push({ lower: start, upper: end });
      start = end + 1;
    }
  } else {
    const totalSpan = oldest - youngest + 1;
    const baseSize = Math.floor(totalSpan / value);
    const remainder = totalSpan % value;

    let currentStart = youngest;
    for (let i = 0; i < value; i += 1) {
      const size = baseSize + (i < remainder ? 1 : 0);
      const currentEnd = currentStart + size - 1;
      computed.push({ lower: currentStart, upper: currentEnd });
      currentStart = currentEnd + 1;
    }
  }

  // Add upper bound band (oldest + 1 to 150)
  if (oldest < 150) {
    computed.push({ lower: oldest + 1, upper: 150 });
  }

  return computed;
}

function createNewAgeBand(
  lastBand: { lower: number; upper: number } | undefined,
  isCriticalIllness: boolean,
): { lower: number; upper: number } {
  if (isCriticalIllness) {
    const newAge = lastBand ? lastBand.upper + 10 : 25;
    return { lower: newAge, upper: newAge };
  }
  const newLower = lastBand ? lastBand.upper + 1 : 0;
  return { lower: newLower, upper: newLower + 4 };
}

// Extracted so the visibility check doesn't add to RatesTab's own cyclomatic
// complexity — Flat and Age Banded render their AD&D section internally, so
// this is only reached for Tiered/PEPM life products.
function TieredPepmAdndSection({
  show,
  formData,
  setFormData,
  coverageType,
}: {
  show: boolean;
  formData: RateScheduleFormData;
  setFormData: React.Dispatch<React.SetStateAction<RateScheduleFormData>>;
  coverageType?: string;
}): JSX.Element | null {
  if (!show) return null;
  return (
    <AdndRateConfigSection
      formData={formData}
      setFormData={setFormData}
      coverageType={coverageType}
    />
  );
}

export function RatesTab({
  formData,
  setFormData,
  coverageType,
}: RatesTabProps): JSX.Element {
  // Filter rate structures based on coverage type
  const availableRateStructures = getAvailableRateStructures(coverageType);

  // Generate dialog state
  const [isGenerateDialogOpen, setIsGenerateDialogOpen] = useState(false);
  const [generateYoungestAge, setGenerateYoungestAge] = useState("");
  const [generateOldestAge, setGenerateOldestAge] = useState("");
  const [generateMethod, setGenerateMethod] = useState<"age-gap" | "bands">(
    "age-gap",
  );
  const [generateValue, setGenerateValue] = useState("");
  const [generatePreviewRanges, setGeneratePreviewRanges] = useState<
    { lower: number; upper: number }[]
  >([]);

  // Stable keys for AgeBandRow — regenerated only when the band count changes,
  // not on value edits (which is what caused the cursor loss).
  const bandCount = (formData.ageBandRanges || []).length;
  const bandKeys = useMemo(
    () => Array.from({ length: bandCount }, (_, i) => `band-${i}`),
    [bandCount],
  );

  const {
    isAgeBanded,
    isPepm,
    isFlat,
    isCriticalIllness,
    showAgeBandConfig,
    showTierConfig,
    showStructureDetail,
    showAdndConfig,
  } = getRateStructureFlags(formData, coverageType);

  const handleRateStructureChange = (
    value: (typeof RATE_STRUCTURE)[keyof typeof RATE_STRUCTURE],
  ): void => {
    setFormData((prev: RateScheduleFormData) =>
      applyRateStructureChange(prev, value, coverageType),
    );
  };

  const handleStructureDetailChange = (
    value: "tobacco" | "gender" | "tiered" | "none",
  ): void => {
    setFormData((prev: RateScheduleFormData) => {
      const tierCount = prev.numberOfTiers || DEFAULT_TIER_COUNT;
      return {
        ...prev,
        structureDetail: value,
        // If switching to tiered, set default tiers
        numberOfTiers: value === "tiered" ? tierCount : undefined,
        selectedTiers:
          value === "tiered"
            ? prev.selectedTiers || DEFAULT_TIERS_BY_COUNT[tierCount]
            : undefined,
      };
    });
  };

  const handleNumberOfTiersChange = (count: number): void => {
    // Use predefined default tiers for each count, or empty array for 6 tiers
    const defaultTiers = DEFAULT_TIERS_BY_COUNT[count] || Array(count).fill("");
    setFormData((prev: RateScheduleFormData) => ({
      ...prev,
      numberOfTiers: count,
      selectedTiers: defaultTiers,
    }));
  };

  const handleTierSelectionChange = (index: number, value: string): void => {
    setFormData((prev: RateScheduleFormData) => {
      const newTiers = [...(prev.selectedTiers || [])];
      newTiers[index] = value;
      return { ...prev, selectedTiers: newTiers };
    });
  };

  // Handler for EMP & Spouse Rates Differ change (age-banded only)
  const handleEmpSpouseRatesDifferChange = (
    value: "yes" | "no" | "na",
  ): void => {
    setFormData((prev: RateScheduleFormData) => ({
      ...prev,
      empSpouseRatesDiffer: value,
    }));
  };

  const handleAgeBandChange = (
    index: number,
    field: "lower" | "upper",
    value: string,
  ): void => {
    const numValue = parseInt(value, 10) || 0;
    setFormData((prev: RateScheduleFormData) => {
      const newBands = [...(prev.ageBandRanges || [])];
      newBands[index] = { ...newBands[index], [field]: numValue };
      return { ...prev, ageBandRanges: newBands };
    });
  };

  // Handler for single-age band changes (used for Critical Illness)
  // Sets both lower and upper to the same value
  const handleSingleAgeBandChange = (index: number, value: string): void => {
    const numValue = parseInt(value, 10) || 0;
    setFormData((prev: RateScheduleFormData) => {
      const newBands = [...(prev.ageBandRanges || [])];
      newBands[index] = { lower: numValue, upper: numValue };
      return { ...prev, ageBandRanges: newBands };
    });
  };

  const handleAddAgeBand = (): void => {
    setFormData((prev: RateScheduleFormData) => {
      const bands = prev.ageBandRanges || [];
      const lastBand = bands[bands.length - 1];
      const newBand = createNewAgeBand(lastBand, isCriticalIllness);
      return { ...prev, ageBandRanges: [...bands, newBand] };
    });
  };

  const handleRemoveAgeBand = (index: number): void => {
    setFormData((prev: RateScheduleFormData) => {
      const filtered = (prev.ageBandRanges ?? []).filter(
        (_: { lower: number; upper: number }, i: number) => i !== index,
      );
      // The first band is always displayed as "<upper" (lower is implicitly 0)
      // for non-CI age-banded ranges. If we removed it, the new first band may
      // carry its old lower value, producing an invalid range (e.g. "20-19").
      // CI bands are single-age (lower === upper) and do not have this invariant.
      if (index === 0 && !isCriticalIllness && filtered.length > 0) {
        filtered[0] = { ...filtered[0], lower: 0 };
      }
      return { ...prev, ageBandRanges: filtered };
    });
  };

  const handleOpenGenerateDialog = (): void => {
    setIsGenerateDialogOpen(true);
  };

  const handleCloseGenerateDialog = (): void => {
    setIsGenerateDialogOpen(false);
  };

  const computeGenerateRanges = useCallback(
    (): { lower: number; upper: number }[] =>
      computeAgeBandRanges({
        youngest: Number(generateYoungestAge),
        oldest: Number(generateOldestAge),
        value: Number(generateValue),
        method: generateMethod,
      }),
    [generateYoungestAge, generateOldestAge, generateValue, generateMethod],
  );

  const handleApplyGenerate = (): void => {
    const computed = computeGenerateRanges();

    if (!computed.length) {
      setIsGenerateDialogOpen(false);
      return;
    }

    setFormData((prev: RateScheduleFormData) => ({
      ...prev,
      ageBandRanges: computed,
    }));
    setIsGenerateDialogOpen(false);
  };

  // Update preview whenever generate inputs change
  useEffect(() => {
    setGeneratePreviewRanges(computeGenerateRanges());
  }, [computeGenerateRanges]);

  const ageBands = formData.ageBandRanges || [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Rate Structure Selection */}
      <FormControl component="fieldset">
        <FormLabel component="legend">Rate Structure</FormLabel>
        <RadioGroup
          row
          value={formData.rateStructure || ""}
          onChange={(e) =>
            handleRateStructureChange(
              e.target.value as "tiered" | "ageBanded" | "flat" | "pepm",
            )
          }
        >
          {availableRateStructures.map((structure) => (
            <FormControlLabel
              key={structure.value}
              value={structure.value}
              control={<Radio color="brandPrimary" />}
              label={structure.label}
            />
          ))}
        </RadioGroup>
      </FormControl>

      {/* Number of Tiers Selection (for Tiered or Age Banded + Tiered) */}
      {showTierConfig && (
        <FormControl component="fieldset">
          <FormLabel component="legend">Number of Tiers</FormLabel>
          <RadioGroup
            row
            value={(formData.numberOfTiers || DEFAULT_TIER_COUNT).toString()}
            onChange={(e) =>
              handleNumberOfTiersChange(parseInt(e.target.value, 10))
            }
          >
            {TIER_COUNTS.map((count) => (
              <FormControlLabel
                key={count}
                value={count.toString()}
                control={<Radio color="brandPrimary" />}
                label={count.toString()}
              />
            ))}
          </RadioGroup>
        </FormControl>
      )}

      {/* Coverage Tier Dropdowns (for Tiered or Age Banded + Tiered) */}
      {showTierConfig && formData.numberOfTiers && (
        <Box sx={{ border: 1, borderColor: "divider", p: 2, borderRadius: 1 }}>
          <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 2 }}>
            Coverage Tiers
          </Typography>
          <Stack spacing={2}>
            {Array.from({ length: formData.numberOfTiers }).map((_, index) => {
              // Get tiers already selected in other dropdowns (excluding current index)
              const selectedInOtherDropdowns = (formData.selectedTiers || [])
                .filter((_, i) => i !== index)
                .filter(Boolean);
              const tierValue =
                formData.selectedTiers?.[index] || `empty-${index}`;

              // Check if any employeeDependent variant is selected ANYWHERE
              // (we need to check all tiers to enforce mutual exclusion globally)
              const allSelectedTiers = formData.selectedTiers || [];
              const selectedEmployeeDependentVariant = allSelectedTiers.find(
                (t) =>
                  EMPLOYEE_DEPENDENT_VARIANTS.includes(
                    t as (typeof EMPLOYEE_DEPENDENT_VARIANTS)[number],
                  ),
              );

              // Current dropdown's selected value (to ensure we never hide our own selection)
              const currentValue = formData.selectedTiers?.[index] || "";

              return (
                <FormControl key={`tier-${tierValue}`} fullWidth size="small">
                  <Select
                    value={formData.selectedTiers?.[index] || ""}
                    onChange={(e) =>
                      handleTierSelectionChange(index, e.target.value)
                    }
                  >
                    {COVERAGE_TIERS.map((tier) => {
                      // Check if this tier is already selected in another dropdown
                      const isSelectedElsewhere =
                        selectedInOtherDropdowns.includes(tier.value);

                      // For employeeDependent variants: hide the other variant if one is already selected
                      // But never hide the current dropdown's own selected value
                      const isEmployeeDependentVariant =
                        EMPLOYEE_DEPENDENT_VARIANTS.includes(
                          tier.value as (typeof EMPLOYEE_DEPENDENT_VARIANTS)[number],
                        );
                      const shouldHideVariant =
                        isEmployeeDependentVariant &&
                        selectedEmployeeDependentVariant &&
                        selectedEmployeeDependentVariant !== tier.value &&
                        tier.value !== currentValue;

                      // Hide the variant entirely if the other one is selected
                      if (shouldHideVariant) {
                        return null;
                      }

                      return (
                        <MenuItem
                          key={tier.value}
                          value={tier.value}
                          disabled={isSelectedElsewhere}
                        >
                          {tier.label}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              );
            })}
          </Stack>
        </Box>
      )}

      {/* Age Banded Configuration (for Age Banded designs) */}
      {showAgeBandConfig && (
        <Box sx={{ border: 1, borderColor: "divider", p: 2, borderRadius: 1 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Typography variant="subtitle1" color="textSecondary">
              Age Banded Configuration
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <IconButton
                size="small"
                onClick={handleAddAgeBand}
                aria-label="Add age band"
                sx={{ color: "action.active" }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleOpenGenerateDialog}
                aria-label="Generate age bands"
                sx={{ color: "action.active" }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
          <Stack spacing={1}>
            {ageBands.map(
              (band: { lower: number; upper: number }, index: number) => (
                <AgeBandRow
                  key={bandKeys[index]}
                  band={band}
                  index={index}
                  isFirstRow={index === 0}
                  isLastRow={index === ageBands.length - 1}
                  isCriticalIllness={isCriticalIllness}
                  isAgeBanded={isAgeBanded}
                  onSingleAgeBandChange={handleSingleAgeBandChange}
                  onAgeBandChange={handleAgeBandChange}
                  onRemoveAgeBand={handleRemoveAgeBand}
                />
              ),
            )}
          </Stack>
        </Box>
      )}

      {/* Generate Age Bands Dialog */}
      <Dialog
        open={isGenerateDialogOpen}
        onClose={handleCloseGenerateDialog}
        fullWidth
        maxWidth="sm"
        hideBackdrop
        sx={{
          "& .MuiDialog-container": {
            alignItems: "center",
            justifyContent: "flex-end",
          },
        }}
      >
        <DialogTitle>Generate Age Bands</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    label="Youngest age"
                    type="number"
                    size="small"
                    value={generateYoungestAge}
                    onChange={(event) =>
                      setGenerateYoungestAge(event.target.value)
                    }
                    fullWidth
                  />
                  <Typography variant="body-medium">to</Typography>
                  <TextField
                    label="Oldest age"
                    type="number"
                    size="small"
                    value={generateOldestAge}
                    onChange={(event) =>
                      setGenerateOldestAge(event.target.value)
                    }
                    fullWidth
                  />
                </Stack>

                <FormControl component="fieldset">
                  <FormLabel component="legend">Method</FormLabel>
                  <RadioGroup
                    name="generate-method"
                    value={generateMethod}
                    onChange={(event) =>
                      setGenerateMethod(
                        event.target.value as "age-gap" | "bands",
                      )
                    }
                  >
                    <FormControlLabel
                      value="age-gap"
                      control={<Radio size="small" color="brandPrimary" />}
                      label="Age gap"
                    />
                    <FormControlLabel
                      value="bands"
                      control={<Radio size="small" color="brandPrimary" />}
                      label="# of bands"
                    />
                  </RadioGroup>
                </FormControl>

                <TextField
                  label={
                    generateMethod === "age-gap"
                      ? "Years per band"
                      : "# of bands"
                  }
                  type="number"
                  size="small"
                  value={generateValue}
                  onChange={(event) => setGenerateValue(event.target.value)}
                  fullWidth
                />
              </Stack>
            </Box>

            <Box sx={{ width: 220 }}>
              <Typography
                variant="subtitle2"
                color="textSecondary"
                sx={{ mb: 1 }}
              >
                Output Preview
              </Typography>
              {generatePreviewRanges.length === 0 ? (
                <Typography variant="body-medium" color="textSecondary">
                  Configure inputs to preview bands.
                </Typography>
              ) : (
                <Stack spacing={0.5}>
                  {generatePreviewRanges.map((range, index) => {
                    const isFirstRow = index === 0;
                    const isLastRow =
                      index === generatePreviewRanges.length - 1;

                    return (
                      <Typography
                        key={`preview-${range.lower}-${range.upper}`}
                        variant="body-medium"
                      >
                        {isFirstRow
                          ? `<${range.upper}`
                          : isLastRow
                            ? `>${range.lower}`
                            : `${range.lower} - ${range.upper}`}
                      </Typography>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="tertiary" onClick={handleCloseGenerateDialog}>
            Cancel
          </Button>
          <Button variant="tertiary" onClick={handleApplyGenerate}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Structure Detail (for Age Banded, Flat, and PEPM - NOT Tiered) */}
      {showStructureDetail && (
        <FormControl component="fieldset">
          <FormLabel component="legend">Structure Detail</FormLabel>
          <RadioGroup
            row
            value={formData.structureDetail || "none"}
            onChange={(e) =>
              handleStructureDetailChange(
                e.target.value as "tobacco" | "gender" | "tiered" | "none",
              )
            }
          >
            {isPepm ? (
              // PEPM only supports None or Gender
              <>
                <FormControlLabel
                  value="none"
                  control={<Radio color="brandPrimary" />}
                  label="None"
                />
                <FormControlLabel
                  value="gender"
                  control={<Radio color="brandPrimary" />}
                  label="Gender"
                />
              </>
            ) : isAgeBanded ? (
              // Age Banded supports None, Tobacco, Gender, Tiered
              AGE_BANDED_STRUCTURE_DETAILS.map((detail) => (
                <FormControlLabel
                  key={detail.value}
                  value={detail.value}
                  control={<Radio color="brandPrimary" />}
                  label={detail.label}
                />
              ))
            ) : (
              // Flat supports None, Tobacco, Gender
              STRUCTURE_DETAILS.map((detail) => (
                <FormControlLabel
                  key={detail.value}
                  value={detail.value}
                  control={<Radio color="brandPrimary" />}
                  label={detail.label}
                />
              ))
            )}
          </RadioGroup>
        </FormControl>
      )}

      {/* Age Banded Dependent Rates Configuration */}
      {isAgeBanded && (
        <AgeBandedRateSection
          formData={formData}
          setFormData={setFormData}
          coverageType={coverageType}
        />
      )}

      {/* EMP & Spouse Rates Differ (for Age Banded only, when dependent rates included) */}
      {isAgeBanded && formData.includeDependentRates && (
        <FormControl component="fieldset">
          <FormLabel component="legend">EMP & Spouse Rates Differ?</FormLabel>
          <RadioGroup
            row
            value={
              formData.empSpouseRatesDiffer || DEFAULT_EMP_SPOUSE_RATES_DIFFER
            }
            onChange={(e) =>
              handleEmpSpouseRatesDifferChange(
                e.target.value as "yes" | "no" | "na",
              )
            }
          >
            {EMP_SPOUSE_OPTIONS.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio color="brandPrimary" />}
                label={option.label}
              />
            ))}
          </RadioGroup>
        </FormControl>
      )}

      {/* ─── Flat Rate Configuration ─── */}
      {isFlat && (
        <FlatRateConfigSection
          formData={formData}
          setFormData={setFormData}
          coverageType={coverageType}
        />
      )}

      <TieredPepmAdndSection
        show={showAdndConfig}
        formData={formData}
        setFormData={setFormData}
        coverageType={coverageType}
      />
    </Box>
  );
}

export default RatesTab;
